import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { AvailableStation } from '../../interface/station-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchApprovedStations,
  updateSingleStation,
} from '../../redux/slice/ApprovedStationManagementSlice';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { fetchRejectedStations } from '../../redux/slice/RejectedStationManagementSlice';
import CustomToggleButton from '../../shared-components/custom-toggle-button/CustomToggleButton';
import DeleteWarning from '../../shared-components/delete-warning/DeleteWarning';
import DisableSwitchWarning from '../../shared-components/disable-warning/DisableWarning';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import PageTitle from '../../shared-components/page-title/PageTitle';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotificaion';
import SwitchButton from '../../shared-components/switch-button-field/SwitchButton';
import stationApi from '../../utils/api/station';

import ApprovedList from './components/ApprovedList';
import PendingList from './components/PendingList';
import RejectedList from './components/RejectedList';
// import StationFilterModal from './components/StationFilter';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
  closeModal: () => void;
}

/**
 * Station management page
 */
function StationManagement() {
  const { t } = useTranslation('station');
  const { page } = useParams<{ page: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { page: approvedPageNumber, limit: approvedLimit } = useAppSelector(
    (state) => state.approvedStation,
  );
  const { page: rejectedPageNumber, limit: rejectedLimit } = useAppSelector(
    (state) => state.rejectStation,
  );

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
    /**
     * Close modal
     */
    closeModal: () => setModalBoxStatus('close'),
  });
  const [openTab, setOpenTab] = useState<number>(0);

  /**
   * Delete station API call
   * @param rowId
   */
  const handleDeleteStation = (rowId?: string | null) => {
    if (rowId) {
      stationApi
        .deleteStation(rowId)
        .then(() => {
          setModalBoxStatus('close');
          setModalBoxDetail({
            title: '',
            content: null,
            notificationModal: false,
            header: true,
            /**
             * Close modal
             */
            closeModal: () => setModalBoxStatus('close'),
          });
          if (page === 'approved') {
            dispatch(fetchApprovedStations({ page: approvedPageNumber, limit: approvedLimit }));
          } else if (page === 'rejected') {
            dispatch(fetchRejectedStations({ page: rejectedPageNumber, limit: rejectedLimit }));
          }
        })
        .catch((error) => {
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || 'Something went wrong!',
              },
            }),
          );
        });
    } else {
      setModalBoxDetail({
        title: '',
        content: null,
        notificationModal: false,
        header: true,
        /**
         * Close modal
         */
        closeModal: () => setModalBoxStatus('close'),
      });
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: 200,
            message: 'Something went wrong!',
          },
        }),
      );
    }
  };

  /**
   * Confirm the status change function
   * @param id
   * @param checked
   */
  const confirmStatusChange = (id: string, checked: boolean) => {
    stationApi
      .editAvailableStatusStation(id, { availabilityStatus: checked })
      .then((response) => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: '',
          content: (
            <SuccessNotification
              title="Status Changed successfully..."
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                dispatch(updateSingleStation(response.data));
              }}
            />
          ),
          notificationModal: true,
          header: false,
          /**
           * Close modal with function to dispatch station details
           */
          closeModal: () => {
            setModalBoxStatus('close');
            dispatch(updateSingleStation(response.data));
          },
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Handles the availability status change for a device
   * Shows a confirmation modal when disabling a device
   * @param {AvailableDevice} device - The device to update
   * @param {boolean} checked - The new availability status
   */
  const handleAvailabilityChange = (device: AvailableStation, checked: boolean) => {
    if (checked) {
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: 'Disable Device Warning',
        content: (
          <DisableSwitchWarning
            selectedId={device._id}
            onCancel={() => setModalBoxStatus('close')}
            message="Making your facility available for public or third-party use opens new revenue opportunities and boosts network accessibility, but also requires sharing control and adhering to service agreements."
            onConfirm={() => confirmStatusChange(device._id, checked)}
          />
        ),
        notificationModal: true,
        header: false,
        /**
         * Close modal
         */
        closeModal: () => setModalBoxStatus('close'),
      });
    } else {
      confirmStatusChange(device._id, checked);
    }
  };
  // /**
  //  * filters
  //  */
  // const handleChangeFilter = (type: string, filterData: StationFilter) => {
  //   setModalBoxStatus('open');
  //   setModalBoxDetail({
  //     title: '',
  //     content: (
  //       <StationFilterModal
  //         handleChange={() => setModalBoxStatus('close')}
  //         filterStatus={type}
  //         oldFilterData={filterData}
  //       />
  //     ),
  //     notificationModal: false,
  //     header: false,
  //     /**
  //      * Close modal
  //      */
  //     closeModal: () => setModalBoxStatus('close'),
  //   });
  // };
  /**
   * Renders the custom data for the availability column
   * @param {AvailableDevice} device - The device data
   * @returns {JSX.Element} The rendered availability toggle cell
   */
  const renderAvailability = (device: AvailableStation) => (
    <div className="flex justify-center">
      <div key={device.id}>
        <SwitchButton
          checked={device.isAvailable}
          change={(checked: boolean) => handleAvailabilityChange(device, checked)}
        />
      </div>
    </div>
  );

  /**
   * Delete station warning popup
   */
  const handleDeleteStationWarning = (rowId?: string | null) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      content: (
        <DeleteWarning
          selectedRowId={rowId}
          title="Are you sure want to delete this Station?"
          description="Deleting your station is permanent and cannot be undone. All the data, settings, and history will be lost. Are you sure you want to proceed?"
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={handleDeleteStation}
        />
      ),
      notificationModal: true,
      header: false,
      /**
       * Close modal
       */
      closeModal: () => setModalBoxStatus('close'),
    });
  };

  /**
   * Handle the change the page
   * @param index
   */
  const changePageHandle = (index: number) => {
    setOpenTab(index);
    let pageName = '';
    switch (index) {
      case 0:
        pageName = 'approved';
        break;
      case 1:
        pageName = 'pending';
        break;
      case 2:
        pageName = 'rejected';
        break;

      default:
        break;
    }
    window.history.pushState({}, '', `${pageName}`);
  };

  useEffect(() => {
    switch (page) {
      case 'approved':
        changePageHandle(0);
        break;
      case 'pending':
        changePageHandle(1);
        break;
      case 'rejected':
        changePageHandle(2);
        break;

      default:
        break;
    }
  }, []);

  return (
    <div className="station-management-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={modalBoxDetail.closeModal}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="station-management-header">
        <PageTitle
          title={t('stationManagement')}
          buttonLabel="Add Station"
          buttonSvgIcon={<PlusIcon />}
          buttonType="submit"
          onClickButton={() => navigate('../add')}
        />
      </div>
      <div className="tabs">
        <CustomToggleButton
          labels={['Approved', 'Pending', 'Rejected']}
          onToggle={changePageHandle}
          defaultActiveIndex={openTab}
        />
      </div>
      <div className="table-container">
        {openTab === 0 && (
          <ApprovedList
            handleDeleteStationWarning={handleDeleteStationWarning}
            renderAvailability={renderAvailability}
          />
        )}
        {openTab === 1 && <PendingList />}
        {openTab === 2 && <RejectedList handleDeleteStationWarning={handleDeleteStationWarning} />}
      </div>
    </div>
  );
}

export default StationManagement;
